import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { FileText, Download, ArrowLeft, CloudArrowUp, Key } from "@phosphor-icons/react";

export default function PYQ() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [papers, setPapers] = useState([]);
    const [examId, setExamId] = useState(searchParams.get("exam_id") || "");
    const [stage, setStage] = useState(searchParams.get("stage") || "");

    const load = async () => {
        const params = {};
        if (examId) params.exam_id = examId;
        if (stage) params.stage = stage;
        const { data } = await api.get("/pyq-papers", { params });
        setPapers(data);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [examId, stage]);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const grouped = papers.reduce((acc, p) => {
        (acc[p.year] = acc[p.year] || []).push(p);
        return acc;
    }, {});
    const yearsSorted = Object.keys(grouped).sort((a, b) => b - a);

    return (
        <Layout>
            <button onClick={() => navigate(-1)} className="btn-ghost mb-6" data-testid="pyq-back">
                <ArrowLeft size={16} /> Back
            </button>

            <div className="mb-8 text-center">
                <div className="text-xs uppercase tracking-widest text-slate-500">Archive</div>
                <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Previous Year Questions</h1>
            </div>

            <div className="bento-card mb-6">
                <div className="flex flex-wrap gap-4 items-end justify-center">
                    <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-1">Exam Board</label>
                        <select value={examId} onChange={(e) => setExamId(e.target.value)} data-testid="pyq-exam" className="px-3 py-2 rounded-lg bg-white border-2 border-[#BFD3FF] text-sm focus:outline-none focus:border-[#1E3A8A]">
                            <option value="">All</option>
                            <option value="kpsc">KPSC</option>
                            <option value="ksp">KSP</option>
                            <option value="kea">KEA</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-1">Stage</label>
                        <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="pyq-stage" className="px-3 py-2 rounded-lg bg-white border-2 border-[#BFD3FF] text-sm focus:outline-none focus:border-[#1E3A8A]">
                            <option value="">All</option>
                            <option value="prelims">Prelims</option>
                            <option value="mains">Mains</option>
                            <option value="interview">Interview</option>
                        </select>
                    </div>
                    <button onClick={() => navigate("/admin/pyq/upload")} className="btn-ghost text-xs" data-testid="upload-link">
                        <CloudArrowUp size={16} /> Upload paper / key
                    </button>
                </div>
            </div>

            {yearsSorted.length === 0 && (
                <div className="bento-card text-slate-500 text-sm text-center">
                    No papers uploaded yet. Head to <button onClick={() => navigate("/admin/pyq/upload")} className="text-[#1E3A8A] font-semibold underline">Upload</button> to add.
                </div>
            )}

            <div className="space-y-5">
                {yearsSorted.map((y) => {
                    const yearPapers = grouped[y];
                    const questions = yearPapers.filter((p) => (p.paper_type || "question") === "question");
                    const keys = yearPapers.filter((p) => p.paper_type === "answer_key");
                    return (
                        <div key={y} className="bento-card">
                            <div className="flex items-center justify-between mb-4">
                                <div className="font-mono font-black text-3xl text-[#1E3A8A]">{y}</div>
                                <div className="text-xs uppercase tracking-widest text-slate-500">{questions.length} paper{questions.length === 1 ? "" : "s"}</div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {questions.map((p) => {
                                    const linkedKey = keys.find((k) => k.linked_paper_id === p.paper_id);
                                    return (
                                        <div key={p.paper_id} className="rounded-xl border-2 border-[#BFD3FF] hover:border-[#1E3A8A] transition-colors overflow-hidden" data-testid={`paper-${p.paper_id}`}>
                                            <a href={`${backendUrl}/api/pyq-papers/${p.paper_id}/download`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3.5 group">
                                                <FileText size={26} weight="duotone" className="text-[#1E3A8A] shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-slate-900 truncate">{p.title}</div>
                                                    <div className="text-xs text-slate-500 mt-0.5">
                                                        {p.exam_id.toUpperCase()} · {p.stage} {p.paper_number ? `· Paper ${p.paper_number}` : ""}
                                                    </div>
                                                </div>
                                                <Download size={18} weight="duotone" className="text-slate-400 group-hover:text-[#1E3A8A]" />
                                            </a>
                                            {linkedKey && (
                                                <a
                                                    href={`${backendUrl}/api/pyq-papers/${linkedKey.paper_id}/download`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 px-3.5 py-2 bg-emerald-50/60 border-t-2 border-emerald-200 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                                                    data-testid={`key-${linkedKey.paper_id}`}
                                                >
                                                    <Key size={14} weight="duotone" /> View Answer Key
                                                    <Download size={12} className="ml-auto" />
                                                </a>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Layout>
    );
}
